import Button from "components/common/Button";
import Container from "components/common/CustomContainer";
import { filterPage } from "components/common/Filters/FiltersHelper";
import { setPageFilter } from "components/common/Filters/GlobalFilters.slice";
import NoDataBanner from "components/common/NoDataBanner";
import Scroller from "components/common/Scroller/InfiniteScroller";
import Typography from "components/common/Typography";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { PAGE_SIZE, brandDeadlinesType, missingIncodeText } from "utils/CommonConstants";
import { formatToDisplayRange } from "utils/CommonFunctions";
import { routes } from "utils/routes";
import { formTypes } from "utils/Types";
import { fetchBrandDeadlinePlansAsync, fetchMyPlansOverviewAsync } from "../dashboard/HotelUserDashboard.slice";
import { getHotelUserDashboardInncode } from "../dashboard/HotelUserDashboardHelper";
import CreateMyPlan from "../MyPlans/Create";
import BrandDeadline from "./BrandDeadline";
import "./MyDashboard.scss";
import PlanOverview from "./PlanOverview";

const MyPlansAndBrandDeadlines: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scrollRef = useRef<HTMLDivElement>(null);

  const externalHltId = searchParams.get("member");
  const dateRangeFilter = useAppSelector((state) => state.hotelUserDashboardSlice.dateRangeFilter);
  const [plansPageNo, setPlansPageNo] = useState(0);
  const [brandDeadlinePageNo, setBrandDeadlinePageNo] = useState(1);

  const { list, totalRecords } = useAppSelector((state) => state.hotelUserDashboardSlice.myPlansOverview);
  const { list: brandDeadlineList, totalRecords: brandDeadlineTotalRecords } = useAppSelector(
    (state) => state.hotelUserDashboardSlice.brandDeadlines
  );
  const { isAbovePropertyUser } = useAppSelector((state) => state.app.loggedInUserDetails);

  const innCode = getHotelUserDashboardInncode();

  const getMyPlansOverview = (page: number) => {
    dispatch(
      fetchMyPlansOverviewAsync({
        size: PAGE_SIZE,
        externalHltId: externalHltId || "",
        innCode,
        page: page,
        ...(dateRangeFilter?.createdDateFrom && dateRangeFilter)
      })
    );
  };
  const getBrandDeadlinePlanAsync = (page: number) => {
    dispatch(
      fetchBrandDeadlinePlansAsync({
        filter: {
          innCode
        },
        pagination: {
          page: page,
          size: PAGE_SIZE
        },
        planType: [brandDeadlinesType],
        sort: {
          property: "brandDeadline",
          direction: "asc"
        },
        externalHltId
      })
    );
  };

  useEffect(() => {
    if (innCode !== missingIncodeText && innCode && plansPageNo > 0) {
      getMyPlansOverview(plansPageNo);
    }
  }, [plansPageNo]);

  useEffect(() => {
    if (innCode !== missingIncodeText && brandDeadlinePageNo > 1) {
      getBrandDeadlinePlanAsync(brandDeadlinePageNo);
    }
  }, [brandDeadlinePageNo]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    getMyPlansOverview(0);
    setPlansPageNo(0);
  }, [innCode, dateRangeFilter]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    getBrandDeadlinePlanAsync(1);
    setBrandDeadlinePageNo(1);
  }, [innCode]);

  const fetchMore = (container: string) => {
    if (container === "my_plans") {
      setPlansPageNo(plansPageNo + 1);
    } else {
      setBrandDeadlinePageNo(brandDeadlinePageNo + 1);
    }
  };

  return (
    <div className="my_plans-brand_deadline">
      <Container className="my_plans" scroll="content">
        <div className="my_plans-header">
          <Typography variant={"h5"} showBold>
            My Plans Overview
          </Typography>
          {!isAbovePropertyUser && (
            <div className="my_plans-header-buttons">
              <CreateMyPlan
                formType={formTypes.CreatePlanFromDashboard}
                label="Add New Plan"
                showMenu
                buttonSize="Small"
              />
              <Button
                variant="secondary"
                label="Go to My Plans"
                onClick={() => {
                  if (dateRangeFilter?.createdDateTo)
                    dispatch(
                      setPageFilter({
                        pageName: filterPage.inncodePlansDisplay,
                        modifiedFilter: [
                          {
                            name: "Created Date",
                            key: "createdDate",
                            options: [formatToDisplayRange(dateRangeFilter)]
                          }
                        ]
                      })
                    );
                  navigate(routes.MyPlans);
                }}
                size="Small"
              />
            </div>
          )}
        </div>
        <div id="my_plans-content" className="my_plans-content" ref={scrollRef}>
          {list?.length ? (
            <Scroller
              dataLength={list?.length}
              loadMore={() => fetchMore("my_plans")}
              totalList={totalRecords}
              label="Plans"
              scrollableTarget={"my_plans-content"}
            >
              {list?.map((element: any) => {
                return <PlanOverview {...element} />;
              })}
            </Scroller>
          ) : (
            <NoDataBanner noDataMessage="You still need to add plans. No problem! Review Suggested Plans, search the Library, or retake the Wizard to find your biggest opportunities." />
          )}
        </div>
      </Container>
      <Container className="brand_deadline" scroll="content">
        <div className="brand_deadline-header">
          <Typography variant={"h5"} showBold>
            Brand Deadlines{" "}
          </Typography>
        </div>
        <div id="brand_deadline-content" className="brand_deadline-content">
          {brandDeadlineList?.length ? (
            <Scroller
              dataLength={brandDeadlineList?.length}
              loadMore={() => fetchMore("brand_deadline")}
              totalList={brandDeadlineTotalRecords}
              label="Plans"
              scrollableTarget={"brand_deadline-content"}
            >
              {brandDeadlineList?.map((element: any) => {
                return <BrandDeadline {...element} />;
              })}
            </Scroller>
          ) : (
            <NoDataBanner noDataMessage="There are no active Brand Deadlines" />
          )}
        </div>
      </Container>
    </div>
  );
};

export default MyPlansAndBrandDeadlines;
